import { MoreHorizontalIcon } from 'lucide-react';

import { useTranslation } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@nucleus-commerce/ui/components/dropdown-menu';

import type { RowAction } from './types';

export interface DataTableRowActionsProps<TData> {
  row: TData;
  actions: ReadonlyArray<RowAction<TData>>;
  /** Screen-reader name of the row: "Actions for Halo ring". */
  label: string;
}

/** The per-row "..." menu in the column that sticks to the end edge. */
function DataTableRowActions<TData>({ row, actions, label }: DataTableRowActionsProps<TData>) {
  const { t } = useTranslation('common');
  const visible = actions.filter((action) => !action.hidden?.(row));
  if (visible.length === 0) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          className="size-7"
          aria-label={t('indexTable.rowActions', { label })}
        >
          <MoreHorizontalIcon aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-40">
        {visible.map((action, index) => (
          <RowActionItem
            key={action.id}
            action={action}
            row={row}
            separator={index > 0 && action.separatorBefore === true}
          />
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function RowActionItem<TData>({
  action,
  row,
  separator,
}: {
  action: RowAction<TData>;
  row: TData;
  separator: boolean;
}) {
  return (
    <>
      {separator ? <DropdownMenuSeparator /> : null}
      <DropdownMenuItem
        variant={action.variant}
        disabled={action.disabled?.(row)}
        onClick={() => void action.onAction(row)}
      >
        {action.icon}
        {action.label}
      </DropdownMenuItem>
    </>
  );
}

export { DataTableRowActions };
